import { PageFrame } from "../../../../../../shared/frontend/layout/PageFrame";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Form, Tooltip } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useAuth } from "../../app/auth/useAuth";
import { Permissions } from "../../app/constants/permissions";
import { spacing } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { documentPreviewValuesEnabled } from "../../app/prefs/documentEditorPreferences";
import { useUiPreferences } from "../../app/prefs/useUiPreferences";
import type { TemplateVariable } from "../../app/templates/templateVariables";
import { useLatestRun } from "../../../../../../shared/frontend/api/useLatestRun";
import { DocumentBlockEditor, type DocumentEditorBlock } from "../DocumentBlockEditor";
import { DocumentPdfPreviewModal } from "../DocumentPdfPreviewModal";
import {
  TemplateEditorWorkspace,
  type TemplateEditorWorkspaceRef,
} from "../TemplateEditorWorkspace";
import { useUndoableValue } from "../blockEditor/useUndoableValue";
import { SendDocumentSettingsDrawer } from "./SendDocumentSettingsDrawer";
import {
  systemDocumentBlockCapabilities,
  systemDocumentBlockLabel,
} from "../../pages/settings/systemDocuments/systemDocumentEditorBlocks";
import { systemDocumentPreviewValueSource } from "../../pages/settings/systemDocuments/systemDocumentPreviewValueSource";
import {
  listDocumentAppearances,
  type DocumentAppearanceDto,
  type DocumentLayoutBlock,
} from "../../pages/settings/documentAppearance/documentAppearanceApi";
import {
  listSystemDocumentTokens,
  previewSystemDocument,
  previewSystemDocumentPdf,
  sendSystemDocument,
  type DocumentBlockOverrides,
  type EffectiveSystemDocumentTextDto,
  type SystemDocumentLayoutBlockDto,
  type SystemDocumentSendResponse,
} from "../../pages/settings/systemDocuments/systemDocumentApi";

/**
 * Das Schreiben ansehen und vor dem Versand ändern — dieselbe Fläche wie die Textpflege unter
 * Einstellungen → Brief- und E-Mail-Vorlagen, mit den Daten dieses Vorgangs.
 *
 * Was hier geschrieben oder gestellt wird, gilt **für diese eine Sendung**. Der gepflegte Textsatz
 * bleibt unberührt; ein Einzelfall soll die Vorlage für alle anderen nicht verändern.
 *
 * Gehen Brief **und** E-Mail heraus, sind beide Fassungen hier bearbeitbar. Führt der Textsatz
 * getrennte Inhalte, schreibt das E-Mail-Blatt in die Mail-Fassung und das Briefblatt in den
 * Brief; sonst gibt es nur einen Text und beide Blätter zeigen ihn.
 */

/** Was diese eine Sendung abweichend stellt. Leer heißt überall „wie gepflegt". */
type ReviewState = {
  /** Der im Briefblatt geschriebene Wortlaut je Baustein. */
  letterBlocks: DocumentBlockOverrides;
  /** Die eigene Fassung der E-Mail; nur bei getrennten Inhalten wirksam. */
  emailBlocks: DocumentBlockOverrides;
  /** Der Betreff — die E-Mail hat kein Blatt, auf dem er stünde. */
  subject: string | null;
  /** Der Titel in der digitalen Akte; leer heißt „Betreff verwenden". */
  documentTitle: string | null;
  /** Ein abweichendes Erscheinungsbild, sofern das Recht dazu vorliegt. */
  documentAppearanceId: string | null;
};

const EMPTY_OVERRIDES: DocumentBlockOverrides = { blocks: {}, hidden: [] };

const EMPTY_STATE: ReviewState = {
  letterBlocks: EMPTY_OVERRIDES,
  emailBlocks: EMPTY_OVERRIDES,
  subject: null,
  documentTitle: null,
  documentAppearanceId: null,
};

/** Ein Mandant führt seine Korrespondenz in einer Sprache; dieselbe Vorgabe wie in der Textpflege. */
const OUTPUT_LANGUAGE = "de";

/** Wie lange nach einer abgeschlossenen Textbearbeitung gewartet wird, bevor der Server neu rendert. */
const PREVIEW_DEBOUNCE_MS = 300;

export type SendDocumentReviewProps = {
  open: boolean;
  /** Die Dokumentart, etwa `rest-period-confirmation`. */
  kind: string;
  /** Der Vorgang, zu dem das Schreiben gehört — er muss gespeichert sein. */
  referenceId: string;
  /** Der gewählte Textsatz. */
  variantId?: string | null;
  /** Ob die Sendung als E-Mail hinausgeht; sonst entsteht nur der Brief. */
  sendEmail: boolean;
  /** Überschrift; ohne Angabe die allgemeine Bezeichnung. */
  title?: string;
  onCancel: () => void;
  onSent: (result: SystemDocumentSendResponse) => void;
};

export type SendDocumentReviewRequest = Omit<SendDocumentReviewProps, "open">;

/**
 * Immer als Arbeitsfläche im Seiteninhalt: Ein Texteditor mit Blattansicht braucht die volle
 * Breite. Die aufrufende Seite ersetzt dafür ihren Inhalt (`documentReview`-Zustand); ein
 * Drawer-Rückfall ist bewusst nicht vorgesehen.
 */
export function SendDocumentReviewWorkspace({
  open,
  kind,
  referenceId,
  variantId,
  sendEmail,
  title,
  onCancel,
  onSent,
}: SendDocumentReviewProps) {
  const auth = useAuth();
  const { text } = useI18n();
  const uiPreferences = useUiPreferences();
  const startPreviewRun = useLatestRun();
  const [workspaceForm] = Form.useForm();
  const workspaceRef = React.useRef<TemplateEditorWorkspaceRef>(null);
  const previewRef = React.useRef<HTMLIFrameElement>(null);

  const canChooseAppearance = auth.hasPermission(Permissions.TemplatesAppearanceOnSend);

  const {
    value: state,
    set: setState,
    reset: resetState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoableValue<ReviewState>(EMPTY_STATE);
  const stateRef = React.useRef(state);
  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const [letterHtml, setLetterHtml] = React.useState("");
  const [emailHtml, setEmailHtml] = React.useState("");
  const [letterBlocks, setLetterBlocks] = React.useState<SystemDocumentLayoutBlockDto[]>([]);
  const [emailBlocks, setEmailBlocks] = React.useState<SystemDocumentLayoutBlockDto[]>([]);
  const [templateValues, setTemplateValues] = React.useState<Record<string, string> | null>(null);
  const [hasSeparateEmailContent, setHasSeparateEmailContent] = React.useState(false);
  const [variables, setVariables] = React.useState<TemplateVariable[]>([]);
  const [appearances, setAppearances] = React.useState<DocumentAppearanceDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfData, setPdfData] = React.useState<Uint8Array | null>(null);
  const [writing, setWriting] = React.useState(false);
  const [surface, setSurface] = React.useState<"letter" | "email">("letter");
  /**
   * Zählt die Anlässe, die Ansicht neu zu holen: Öffnen, Verlassen eines Blocks, Umschalten der
   * Vorschauwerte, geänderte Einstellungen. Beim Tippen bleibt sie stehen — sonst spränge die
   * Schreibmarke.
   */
  const [renderToken, setRenderToken] = React.useState(0);

  const previewValuesEnabled = documentPreviewValuesEnabled(uiPreferences.prefs, "send");

  const refresh = React.useCallback(
    async (values: ReviewState) => {
      const isCurrent = startPreviewRun();
      const result = await previewSystemDocument(auth.api, kind, {
        referenceId,
        variantId: variantId ?? null,
        language: OUTPUT_LANGUAGE,
        subject: values.subject,
        documentAppearanceId: values.documentAppearanceId,
        blockOverrides: values.letterBlocks,
        emailBlockOverrides: values.emailBlocks,
        resolveTemplateVariables: previewValuesEnabled,
      });
      if (!isCurrent()) return;
      setLetterHtml(result.contentHtml);
      setEmailHtml(result.emailHtml);
      setLetterBlocks(result.blocks ?? []);
      setEmailBlocks(result.emailBlocks ?? result.blocks ?? []);
      setTemplateValues(result.templateValues ?? null);
      setHasSeparateEmailContent(result.hasSeparateEmailContent === true);
    },
    [auth.api, kind, previewValuesEnabled, referenceId, startPreviewRun, variantId]
  );

  /**
   * Ein erneutes Öffnen beginnt bewusst wieder beim gepflegten Text: Eine stehengebliebene
   * Formulierung der vorigen Sendung wäre sonst still Teil der nächsten.
   *
   * Zurückgesetzt wird beim Rendern und nicht im Effect — ein Effect zeigte erst einen Durchlauf
   * lang den Stand des vorigen Vorgangs.
   */
  const session = open ? `${kind}:${referenceId}:${variantId ?? ""}` : null;
  const [renderedSession, setRenderedSession] = React.useState<string | null>(null);
  if (renderedSession !== session) {
    setRenderedSession(session);
    setLoading(session != null);
    resetState(EMPTY_STATE);
    // Der Abruf laeuft ueber denselben Weg wie jede spaetere Aenderung; ein zweiter Ladepfad
    // waere eine zweite Stelle, an der Reihenfolge und Abbruch stimmen muessten.
    setRenderToken((current) => current + 1);
  }

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listSystemDocumentTokens(auth.api)
      .then((tokens) => {
        if (cancelled) return;
        setVariables(tokens.map((token) => ({
          key: token.key,
          group: { de: token.groupDe, en: token.groupEn },
          label: { de: token.labelDe, en: token.labelEn },
        })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [auth.api, open]);

  /**
   * Die Erscheinungsbilder werden nur geholt, wenn sie auch wählbar sind — ohne das Recht wäre es
   * ein Abruf für ein gesperrtes Feld.
   */
  React.useEffect(() => {
    if (!open || !canChooseAppearance) return;
    let cancelled = false;
    void listDocumentAppearances(auth.api)
      .then((entries) => {
        if (!cancelled) setAppearances(entries ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [auth.api, canChooseAppearance, open]);

  /**
   * Solange im Blatt geschrieben wird, wird die Ansicht **nicht** neu geholt: Sie hängt an den
   * Werten, und jedes getippte Zeichen ändert sie. Sobald die Hand vom Text ist, holt dieser
   * Effekt den Abgleich nach.
   */
  React.useEffect(() => {
    if (!open || writing || renderToken === 0) return;
    const timer = window.setTimeout(() => {
      void refresh(stateRef.current)
        .catch(() => {})
        .finally(() => setLoading(false));
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, refresh, renderToken, writing]);

  /** Angeboten wird, was das Format hergibt — und die Mail nur, wenn sie auch hinausgeht. */
  const surfaces = React.useMemo<Array<"letter" | "email">>(() => {
    const available: Array<"letter" | "email"> = [];
    if (letterHtml) available.push("letter");
    if (sendEmail && emailHtml) available.push("email");
    return available;
  }, [emailHtml, letterHtml, sendEmail]);

  const shownSurface = surfaces.includes(surface) ? surface : (surfaces[0] ?? "letter");
  const activeBlocks = shownSurface === "email" ? emailBlocks : letterBlocks;

  /**
   * In welchen Satz geschrieben wird. Ohne getrennte Inhalte gibt es nur einen — dann ändert auch
   * das E-Mail-Blatt den gemeinsamen Text, und beide Ausgaben zeigen ihn.
   */
  const writesToEmail = hasSeparateEmailContent && shownSurface === "email";
  const activeOverrides = writesToEmail ? state.emailBlocks : state.letterBlocks;

  const blockOf = React.useCallback(
    (blockId: string) => activeBlocks.find((item) => item.block.id === blockId)?.block ?? null,
    [activeBlocks]
  );
  const blockKeyOf = React.useCallback(
    (blockId: string) => blockOf(blockId)?.key ?? null,
    [blockOf]
  );

  const hiddenKeys = activeOverrides.hidden;

  /**
   * Was an einem Baustein änderbar ist, entscheidet `systemDocumentBlockCapabilities` — dieselbe
   * Funktion, die auch die Textpflege fragt. Der Einzelversand hat hier keine eigene Regel; er
   * unterscheidet sich allein darin, **wohin** die Änderung geschrieben wird.
   */
  const editorBlocks = React.useMemo<DocumentEditorBlock[]>(
    () => activeBlocks.map(({ block }) => {
      const can = systemDocumentBlockCapabilities(block);
      return {
        id: block.id,
        key: block.key,
        label: systemDocumentBlockLabel(block.type),
        // Die Bausteinfolge gehört der Vorlage; auch die Textpflege verschiebt hier nichts.
        movable: false,
        hidden: can.hideable && block.key != null && hiddenKeys.includes(block.key),
        writable: can.writable,
        logo: can.logo,
        formattable: can.formattable,
        format: {
          bold: block.bold,
          italic: block.italic,
          underline: block.underline,
          fontFamily: block.fontFamily,
          fontSizePt: block.fontSizePt,
          textColor: block.type === "footer" ? block.footerTextColor : block.textColor,
          backgroundColor: block.type === "footer" ? block.footerBackgroundColor : block.backgroundColor,
          alignment: block.textAlignment ?? 0,
        },
      };
    }),
    [activeBlocks, hiddenKeys]
  );

  const previewValueSource = React.useCallback(
    (blockId: string, field: string | null) => systemDocumentPreviewValueSource(
      blockOf(blockId),
      field,
      activeOverrides,
      { subject: state.subject ?? "", dataBlockSettings: { blocks: {} } } as EffectiveSystemDocumentTextDto
    ),
    [activeOverrides, blockOf, state.subject]
  );

  /** Eine Änderung am Blatt landet im Satz des gerade sichtbaren Kanals — nie in der Vorlage. */
  const patchBlockOverride = React.useCallback(
    (blockKey: string, patch: Partial<DocumentLayoutBlock>) => {
      const current = stateRef.current;
      const target = writesToEmail ? "emailBlocks" : "letterBlocks";
      const overrides = current[target];
      setState({
        ...current,
        [target]: {
          ...overrides,
          blocks: { ...overrides.blocks, [blockKey]: { ...overrides.blocks[blockKey], ...patch } },
        },
      });
      workspaceRef.current?.signalDirtyEvaluation();
    },
    [setState, writesToEmail]
  );

  /**
   * Der Betreff-Baustein des Briefs. Im Blatt geschrieben geht sein Wortlaut dem im Drawer
   * gestellten Betreff vor — damit es nur eine Antwort auf die Frage „wie lautet der Betreff" gibt,
   * nimmt das Feld diese Abweichung zurück. Dieselbe Regel wie in der Textpflege.
   */
  const subjectBlockKey = React.useMemo(
    () => letterBlocks.find(({ block }) => block.type === "subject")?.block.key ?? null,
    [letterBlocks]
  );

  /** Das PDF bleibt die maßgebliche Briefausgabe; die HTML-Fläche ist nur die Arbeitsvorschau. */
  const openPdf = React.useCallback(async () => {
    setPdfLoading(true);
    try {
      const values = stateRef.current;
      const blob = await previewSystemDocumentPdf(auth.api, kind, {
        referenceId,
        variantId: variantId ?? null,
        language: OUTPUT_LANGUAGE,
        subject: values.subject,
        documentAppearanceId: values.documentAppearanceId,
        blockOverrides: values.letterBlocks,
        emailBlockOverrides: values.emailBlocks,
        // Die Druckvorschau zeigt denselben Stand wie das Blatt, einschließlich der noch nicht
        // abgesendeten Änderungen.
        resolveTemplateVariables: true,
      });
      setPdfData(new Uint8Array(await blob.arrayBuffer()));
    } finally {
      setPdfLoading(false);
    }
  }, [auth.api, kind, referenceId, variantId]);

  /** Ein weggelassener Baustein — für diese Sendung, nicht für die Vorlage. */
  const setBlockVisible = React.useCallback(
    (blockKey: string, visible: boolean) => {
      const current = stateRef.current;
      const target = writesToEmail ? "emailBlocks" : "letterBlocks";
      const overrides = current[target];
      const hidden = overrides.hidden.filter((entry) => entry !== blockKey);
      setState({
        ...current,
        [target]: { ...overrides, hidden: visible ? hidden : [...hidden, blockKey] },
      });
      workspaceRef.current?.signalDirtyEvaluation();
    },
    [setState, writesToEmail]
  );

  const handleSend = React.useCallback(async () => {
    setSending(true);
    try {
      const values = stateRef.current;
      const result = await sendSystemDocument(auth.api, kind, {
        referenceId,
        sendEmail,
        variantId: variantId ?? null,
        language: OUTPUT_LANGUAGE,
        subject: values.subject,
        documentTitle: values.documentTitle,
        // Ohne das Recht bleibt das Feld gesperrt; die Sendung nimmt dann das Briefpapier der
        // Vorlage. Das Backend prüft dasselbe noch einmal.
        documentAppearanceId: canChooseAppearance ? values.documentAppearanceId : null,
        blockOverrides: values.letterBlocks,
        emailBlockOverrides: values.emailBlocks,
      });
      onSent(result);
    } finally {
      setSending(false);
    }
  }, [auth.api, canChooseAppearance, kind, onSent, referenceId, sendEmail, variantId]);

  const renderEditor = (headerExtra?: React.ReactNode) => (
    <DocumentBlockEditor
      // Eine Sendung ändert Wortlaut und Auszeichnung des einzelnen Schreibens; Aufbau und
      // Bausteinfolge gehören der Vorlage. Damit entfallen Bausteinleiste, Griff und
      // „Baustein hinzufügen".
      allowDesign={false}
      previewRef={previewRef}
      blocks={editorBlocks}
      previewHtml={shownSurface === "letter" ? letterHtml : emailHtml}
      loading={loading}
      surface={shownSurface}
      surfaces={surfaces}
      onSurfaceChange={(value) => {
        if (value === "whatsapp") return;
        setSurface(value);
      }}
      previewValuesEnabled={previewValuesEnabled}
      onPreviewValuesChange={(enabled) => {
        uiPreferences.patchPrefs({ documentEditors: { sendPreviewValues: enabled } });
        setRenderToken((current) => current + 1);
      }}
      previewValueSource={previewValueSource}
      previewTemplateValues={templateValues ?? undefined}
      canUndo={canUndo}
      canRedo={canRedo}
      onUndo={() => {
        undo();
        workspaceRef.current?.signalDirtyEvaluation();
      }}
      onRedo={() => {
        redo();
        workspaceRef.current?.signalDirtyEvaluation();
      }}
      variables={variables}
      onWritingChange={(value) => {
        setWriting(value);
        if (!value) setRenderToken((current) => current + 1);
      }}
      onToggleVisible={(blockId, visible) => {
        const key = blockKeyOf(blockId);
        const block = blockOf(blockId);
        if (key != null && block != null && systemDocumentBlockCapabilities(block).hideable) {
          setBlockVisible(key, visible);
        }
      }}
      onPatchBlock={(blockId, patch) => {
        const key = blockKeyOf(blockId);
        const block = blockOf(blockId);
        if (key != null && block != null && systemDocumentBlockCapabilities(block).formattable) {
          patchBlockOverride(key, patch as Partial<DocumentLayoutBlock>);
        }
      }}
      onTextChange={(blockId, value) => {
        const key = blockKeyOf(blockId);
        const block = blockOf(blockId);
        if (key == null || block == null || !systemDocumentBlockCapabilities(block).writable) return;
        patchBlockOverride(key, { text: value });
      }}
      headerExtra={headerExtra}
    />
  );

  React.useEffect(() => {
    if (!open || loading || workspaceRef.current?.hasInitialSnapshot()) return;
    workspaceRef.current?.setInitialSnapshot();
  }, [loading, open]);

  return (
    <TemplateEditorWorkspace
      ref={workspaceRef}
      open={open}
      mode="edit"
      title={title || text("Review before sending")}
      backLabel="Back"
      form={workspaceForm}
      editorRef={previewRef as never}
      getCurrentBody={() => JSON.stringify(stateRef.current)}
      buildSnapshot={() => ({ review: stateRef.current })}
      saving={sending}
      onClose={onCancel}
      onSave={handleSend}
      saveText="Send"
      extraActions={
        letterHtml ? (
          <Tooltip title={shownSurface === "email" ? text("Print preview is only available in letter view") : undefined}>
            <Button
              icon={<PrinterOutlined />}
              loading={pdfLoading}
              disabled={shownSurface === "email"}
              onClick={() => openPdf()}
            >
              {text("Print preview")}
            </Button>
          </Tooltip>
        ) : null
      }
      // Dieselbe Aufteilung wie die Textpflege: Kopf und Speichern in der Fachkarte, der Titel
      // als Seitenüberschrift über dem Blatt.
      headerInBody
      hideBackButton
      hideHeaderTitle
      renderBody={({ header, requestClose }) => (
        <PageFrame objectTitle={title || text("Review before sending")} breadcrumb={<> <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={requestClose}
            style={{ marginBottom: spacing.sm, paddingInline: 0 }}
          >
            {text("Back")}
          </Button> </>}><div style={{ minWidth: 0 }}>{renderEditor(header)}<DocumentPdfPreviewModal
            open={pdfData != null}
            title={text("Print preview")}
            fileName={title ? `${title}.pdf` : undefined}
            fileData={pdfData}
            onCancel={() => setPdfData(null)}
          /></div></PageFrame>
      )}
      renderSettings={({ open: settingsOpen, closeSettings }) => (
        <SendDocumentSettingsDrawer
          open={settingsOpen}
          appearances={appearances}
          canChooseAppearance={canChooseAppearance}
          initialValues={{
            documentTitle: state.documentTitle,
            subject: state.subject,
            documentAppearanceId: state.documentAppearanceId,
          }}
          onCancel={closeSettings}
          onFinish={(values) => {
            const current = stateRef.current;
            const nextSubject = values.subject ?? null;
            // Ein im Blatt geschriebener Betreff bliebe sonst stehen und das Feld wirkungslos.
            const letterOverrides = subjectBlockKey != null
              && nextSubject !== current.subject
              && current.letterBlocks.blocks[subjectBlockKey]?.text != null
              ? {
                ...current.letterBlocks,
                blocks: {
                  ...current.letterBlocks.blocks,
                  [subjectBlockKey]: { ...current.letterBlocks.blocks[subjectBlockKey], text: null },
                },
              }
              : current.letterBlocks;
            setState({
              ...current,
              letterBlocks: letterOverrides,
              documentTitle: values.documentTitle?.trim() || null,
              subject: nextSubject,
              documentAppearanceId: canChooseAppearance ? values.documentAppearanceId ?? null : null,
            });
            workspaceRef.current?.signalDirtyEvaluation();
            setRenderToken((token) => token + 1);
            return closeSettings();
          }}
        />
      )}
    />
  );
}

